import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Gleap from "gleap";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  HelpCircle,
  LogOut,
  MessageCircle,
} from "lucide-react";

interface LoadFailurePageProps {
  reason?: "no_account" | "load_error" | "deactivated";
}

export function LoadFailurePage({
  reason = "load_error",
}: LoadFailurePageProps) {
  const { logout, user } = useAuth0();

  const handleContactSupport = () => {
    Gleap.open();
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isNoAccount = reason === "no_account";
  const isDeactivated = reason === "deactivated";

  return (
    <main className="grid min-h-screen place-items-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              {isDeactivated ? (
                <Ban className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              ) : isNoAccount ? (
                <HelpCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              ) : (
                <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white">
              {isDeactivated
                ? "Account Deactivated"
                : isNoAccount
                  ? "Account Not Found"
                  : "Unable to Load"}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {isDeactivated
                ? "Your access to Hub has been deactivated."
                : isNoAccount
                  ? "We couldn't find a Hub account associated with your login."
                  : "We encountered an issue loading your account data."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6 pt-4">
            {isDeactivated ? (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left dark:border-amber-900/40 dark:bg-amber-900/20">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="space-y-1">
                  <p className="font-semibold text-amber-900 dark:text-amber-100">
                    Your account is no longer active.
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300/90">
                    If you believe this is a mistake, please contact your
                    administrator or use the button below.
                  </p>
                </div>
              </div>
            ) : isNoAccount ? (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4 text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-2">Need access to Hub?</p>
                <p>
                  If you believe you should have access, please contact our
                  {/* eslint-disable-next-line react/no-unescaped-entities */}
                  support team using the button below. We'll help get you set up
                  with the right account.
                </p>
              </div>
            ) : (
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-4 text-sm text-slate-600 dark:text-slate-300">
                <p>
                  This could be a temporary issue. If the problem persists,
                  please reach out to our support team for assistance.
                </p>
              </div>
            )}

            {user?.email && (
              <div className="text-center text-sm text-slate-500 dark:text-slate-400">
                Logged in as:{" "}
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  {user.email}
                </span>
              </div>
            )}

            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={handleContactSupport}
                size="lg"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Contact Support
              </Button>

              <Button
                className="w-full"
                onClick={handleLogout}
                size="lg"
                variant="outline"
              >
                <LogOut className="mr-2 h-5 w-5" />
                Sign Out
              </Button>
            </div>

            {isNoAccount && (
              <p className="text-center text-xs text-slate-400 dark:text-slate-500">
                Our support team typically responds within 1 business day.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
